import { GOOGLE_PAY_COUNTRY_CONFIG, getGooglePayCountryKey, getGooglePayCountryMeta } from './countryConfig'

const GOOGLE_PAY_SCRIPT_SRC = 'https://pay.google.com/gp/p/js/pay.js'
let scriptPromise = null
let cachedClient = null
let cachedClientEnv = null

export const loadGooglePayScript = () => {
    if (scriptPromise) return scriptPromise

    scriptPromise = new Promise((resolve, reject) => {
        if (window.google?.payments?.api) return resolve()

        const existing = document.querySelector(`script[src="${GOOGLE_PAY_SCRIPT_SRC}"]`)
        if (existing) {
            if (existing.dataset.googlePayLoaded === 'true') return resolve()
            existing.addEventListener('load', () => resolve())
            existing.addEventListener('error', () => reject(new Error('Failed to load Google Pay script')))
            return
        }

        const script = document.createElement('script')
        script.src = GOOGLE_PAY_SCRIPT_SRC
        script.async = true
        script.onload = () => {
            script.dataset.googlePayLoaded = 'true'
            resolve()
        }
        script.onerror = () => reject(new Error('Failed to load Google Pay script'))
        document.head.appendChild(script)
    })

    return scriptPromise
}

const getEnvironment = () => (window.HAS_GOOGLE_PAY_PRODUCTION === 'true' ? 'PRODUCTION' : 'TEST')

export const createPaymentsClient = () => {
    const env = getEnvironment()
    if (cachedClient && cachedClientEnv === env) return cachedClient

    if (!window.google?.payments?.api) {
        console.error('Google Pay script is not loaded')
        return
    }

    cachedClientEnv = env
    cachedClient = new window.google.payments.api.PaymentsClient({ environment: env })
    return cachedClient
}

export const getCountryConfig = () => {
    const key = getGooglePayCountryKey()
    return key ? GOOGLE_PAY_COUNTRY_CONFIG[key] : null
}

export const isReadyToPay = async () => {
    const config = getCountryConfig()
    if (!config) return false

    await loadGooglePayScript()
    const client = createPaymentsClient()
    const res = await client.isReadyToPay({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [{ type: 'CARD', parameters: config }],
    })
    return Boolean(res?.result)
}

export const requestPayment = async (merchantId, totalPrice) => {
    const config = getCountryConfig()
    const meta = getGooglePayCountryMeta()
    if (!merchantId || !config || !meta) {
        console.error('Missing Google Pay merchantId or config')
        return
    }

    const client = createPaymentsClient()
    return client.loadPaymentData({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
            {
                type: 'CARD',
                parameters: config,
                tokenizationSpecification: {
                    type: 'PAYMENT_GATEWAY',
                    parameters: {
                        gateway: meta.gateway,
                        gatewayMerchantId: merchantId,
                    },
                },
            },
        ],
        transactionInfo: {
            totalPriceStatus: 'FINAL',
            totalPrice: `${Number(totalPrice || 0).toFixed(2)}`,
            currencyCode: meta.currencyCode,
            countryCode: meta.countryCode,
        },
        merchantInfo: { merchantName: 'Indigo' },
    })
}

export const extractGooglePayFields = paymentData => {
    const tokenStr = paymentData?.paymentMethodData?.tokenizationData?.token
    const network = paymentData?.paymentMethodData?.info?.cardNetwork
    if (!tokenStr) return null

    let tokenObj = null
    try {
        tokenObj = JSON.parse(tokenStr)
    } catch {}

    return {
        GooglePayKey: tokenObj ? JSON.stringify(tokenObj) : tokenStr,
        Network: network,
        ProtocolVersion: tokenObj?.protocolVersion,
        Signature: tokenObj?.signature,
        SignedMessage: tokenObj?.signedMessage,
    }
}

export const createGooglePayButton = ({ onClick, locale }) => {
    const client = createPaymentsClient()
    return client.createButton({
        onClick,
        buttonType: 'pay',
        buttonLocale: locale,
        buttonColor: 'default',
        buttonRadius: 6,
        buttonBorderType: 'default_border',
        buttonSizeMode: 'fill',
    })
}
